import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Clock, Users, BookOpen, Plus, X, ChevronRight, ChevronLeft, Edit, Download, Table, Timer, Coffee, AlertCircle, FileText } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { MasterSchedule, ClassSchedule, SchedulePeriod, SchoolTime } from '../../types/schedule';
import { getSchoolTimes } from '../../utils/schedule';
import { getClasses } from '../../utils/subjectsClasses';
import { Class } from '../../utils/subjectsClasses';
import { collection, getDocs, doc, setDoc, getDoc, deleteDoc, query, where, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../../firebase/config';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
// @ts-ignore
import 'jspdf-autotable';

// 12-hour format utilities
const formatTo12Hour = (time24: string): string => {
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

const formatTo24Hour = (time12: string): string => {
    const [time, period] = time12.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    let hours24 = hours;

    if (period === 'PM' && hours !== 12) {
        hours24 = hours + 12;
    } else if (period === 'AM' && hours === 12) {
        hours24 = 0;
    }

    return `${hours24.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

const convertTimeInput = (time24: string): string => {
    // Convert 24-hour to 12-hour for input display
    return formatTo12Hour(time24);
};

const convertTimeFromInput = (time12: string): string => {
    // Convert 12-hour to 24-hour for storage
    return formatTo24Hour(time12);
};

interface ProfessionalScheduleBuilderProps {
    onSave: (schedule: Omit<MasterSchedule, 'id' | 'createdAt' | 'updatedAt'>) => void;
    onCancel: () => void;
    schoolTimes?: SchoolTime[];
}

const ProfessionalScheduleBuilder: React.FC<ProfessionalScheduleBuilderProps> = ({ onSave, onCancel, schoolTimes: initialSchoolTimes }) => {
    const { showSuccess, showError } = useToast();

    // State
    const [schoolTimes, setSchoolTimes] = useState<SchoolTime[]>(initialSchoolTimes || []);
    const [classes, setClasses] = useState<Class[]>([]);
    const [loading, setLoading] = useState(true);

    // Form state
    const [currentStep, setCurrentStep] = useState(1);
    const [selectedClass, setSelectedClass] = useState<Class | null>(null);
    const [selectedSchoolTime, setSelectedSchoolTime] = useState<SchoolTime | null>(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        schoolTimeId: '',
        schedules: [] as ClassSchedule[]
    });

    // Modal states
    const [showPeriodModal, setShowPeriodModal] = useState(false);
    const [selectedDay, setSelectedDay] = useState<number>(1); // Monday
    const [selectedPeriod, setSelectedPeriod] = useState<SchedulePeriod | null>(null);
    const [teachers, setTeachers] = useState<any[]>([]);
    const [showTeacherModal, setShowTeacherModal] = useState(false);

    // Delete session modal states
    const [showDeleteSessionModal, setShowDeleteSessionModal] = useState(false);
    const [sessionToDelete, setSessionToDelete] = useState<{ dayOfWeek: number, sessionId: string, sessionName: string } | null>(null);

    // Fetch schedule states
    const [fetchedSchedule, setFetchedSchedule] = useState<ClassSchedule | null>(null);
    const [showScheduleTable, setShowScheduleTable] = useState(false);
    const [fetchingSchedule, setFetchingSchedule] = useState(false);

    // Teacher availability cache
    const [teacherAvailabilityCache, setTeacherAvailabilityCache] = useState<Record<string, {
        data: Record<string, { hasConflict: boolean, conflictDetails: { className: string, day: string, time: string, subject: string } | null }>,
        timestamp: number,
        dayOfWeek: number,
        startTime: string,
        endTime: string
    }>>({});

    // Day status management
    const [dayStatuses, setDayStatuses] = useState<Record<number, {
        isDayOff: boolean;
        copyFromMonday: boolean;
        lastPeriodEndTime: string;
    }>>({});

    // Copy loading state
    const [copyingToDay, setCopyingToDay] = useState<number | null>(null);
    const [showCopyConfirmModal, setShowCopyConfirmModal] = useState(false);
    const [copyTargetDay, setCopyTargetDay] = useState<number | null>(null);

    // Session management
    const [selectedSession, setSelectedSession] = useState<any>(null);
    const [showSessionModal, setShowSessionModal] = useState(false);

    const daysOfWeek = [
        'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
    ];

    useEffect(() => {
        fetchData();
    }, []);

    // Update schoolTimes when prop changes
    useEffect(() => {
        if (initialSchoolTimes && initialSchoolTimes.length > 0) {
            setSchoolTimes(initialSchoolTimes);

            // Auto-select if only one exists and nothing is selected
            if (initialSchoolTimes.length === 1 && !selectedSchoolTime) {
                setSelectedSchoolTime(initialSchoolTimes[0]);
                setFormData(prev => ({ ...prev, schoolTimeId: initialSchoolTimes[0].id }));
            }
        }
    }, [initialSchoolTimes]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Only fetch schoolTimes if not provided as prop
            const fetchPromises: Promise<any>[] = [
                getClasses(),
                fetchTeachers()
            ];

            if (!initialSchoolTimes || initialSchoolTimes.length === 0) {
                fetchPromises.unshift(getSchoolTimes());
            }

            const results = await Promise.all(fetchPromises);

            let schoolTimesData: SchoolTime[];
            let classesData: Class[];
            let teachersData: any[];

            if (!initialSchoolTimes || initialSchoolTimes.length === 0) {
                [schoolTimesData, classesData, teachersData] = results;
                setSchoolTimes(schoolTimesData);
            } else {
                [classesData, teachersData] = results;
                schoolTimesData = initialSchoolTimes;
                // Update state if not already set
                if (schoolTimes.length === 0) {
                    setSchoolTimes(initialSchoolTimes);
                }
            }

            setClasses(classesData);
            setTeachers(teachersData);

            // Auto-select school time if only one exists
            if (schoolTimesData.length === 1) {
                setSelectedSchoolTime(schoolTimesData[0]);
                setFormData(prev => ({ ...prev, schoolTimeId: schoolTimesData[0].id }));
            }
        } catch (error) {
            showError('Error', 'Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const fetchTeachers = async () => {
        try {
            const teachersRef = collection(db, 'teachers');
            const snapshot = await getDocs(teachersRef);
            return snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
        } catch (error) {
            console.error('Error fetching teachers:', error);
            return [];
        }
    };

    const updateTeacherAvailability = async (teacherId: string, periodData: {
        classId: string;
        periodId: string;
        className: string;
        subject: string;
        dayOfWeek: number;
        startTime: string;
        endTime: string;
        status: string;
    }) => {
        try {
            const teacherRef = doc(db, 'teachers', teacherId);
            await updateDoc(teacherRef, {
                periods: arrayUnion(periodData)
            });
            console.log('Teacher availability updated:', teacherId, periodData);
        } catch (error) {
            console.error('Error updating teacher availability:', error);
        }
    };

    const removeTeacherAvailability = async (teacherId: string, periodId: string) => {
        try {
            if (!teacherId) {
                console.warn('No teacherId provided for removal');
                return;
            }

            const teacherRef = doc(db, 'teachers', teacherId);
            const teacherDoc = await getDoc(teacherRef);

            if (teacherDoc.exists()) {
                const teacherData = teacherDoc.data();
                const periods = teacherData.periods || [];

                console.log('Before removal - Teacher periods:', periods);
                console.log('Trying to remove period with ID:', periodId);

                // Filter out the period
                const updatedPeriods = periods.filter((period: any) => {
                    const shouldKeep = period.periodId !== periodId;
                    if (!shouldKeep) {
                        console.log('Found and removing period:', period);
                    }
                    return shouldKeep;
                });

                console.log('After removal - Updated periods:', updatedPeriods);
                console.log('Removed count:', periods.length - updatedPeriods.length);

                if (periods.length === updatedPeriods.length) {
                    console.warn('⚠️ No period was removed! Period ID might not match.');
                    console.warn('Available period IDs:', periods.map((p: any) => p.periodId));
                }

                await updateDoc(teacherRef, {
                    periods: updatedPeriods
                });

                console.log('✅ Teacher availability successfully removed from Firestore');
            } else {
                console.warn('Teacher document not found:', teacherId);
            }
        } catch (error) {
            console.error('❌ Error removing teacher availability:', error);
            throw error; // Re-throw to handle in calling function
        }
    };

    const getCachedAvailability = useCallback((dayOfWeek: number, startTime: string, endTime: string) => {
        const cacheKey = `${dayOfWeek}-${startTime}-${endTime}`;
        const cached = teacherAvailabilityCache[cacheKey];

        // Check if cache is valid (less than 5 minutes old)
        if (cached && (Date.now() - cached.timestamp) < 300000) {
            console.log('Using cached availability data for:', cacheKey);
            return cached.data;
        }

        return null;
    }, [teacherAvailabilityCache]);

    const setCachedAvailability = useCallback((dayOfWeek: number, startTime: string, endTime: string, data: Record<string, { hasConflict: boolean, conflictDetails: { className: string, day: string, time: string, subject: string } | null }>) => {
        const cacheKey = `${dayOfWeek}-${startTime}-${endTime}`;
        setTeacherAvailabilityCache(prev => ({
            ...prev,
            [cacheKey]: {
                data,
                timestamp: Date.now(),
                dayOfWeek,
                startTime,
                endTime
            }
        }));
        console.log('Cached availability data for:', cacheKey);
    }, []);

    const invalidateAvailabilityCache = useCallback(() => {
        setTeacherAvailabilityCache({});
        console.log('Availability cache invalidated');
    }, []);

    const checkTeacherAvailability = useCallback(async (teacherId: string, dayOfWeek: number, startTime: string, endTime: string, excludePeriodId?: string) => {
        try {
            console.log('Checking teacher availability:', teacherId, 'day:', dayOfWeek, 'time:', startTime, '-', endTime);
            const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

            // Check current class schedule first (in-memory)
            const currentSchedule = formData.schedules.find(s => s.classId === selectedClass?.id);
            if (currentSchedule) {
                const conflictInCurrentClass = currentSchedule.periods.find(period => {
                    // Skip the period being edited
                    if (excludePeriodId && period.id === excludePeriodId) return false;

                    // Check if same teacher and same day
                    if (period.teacherId === teacherId && period.dayOfWeek === dayOfWeek) {
                        // Check for time overlap
                        const periodStart = period.startTime;
                        const periodEnd = period.endTime;

                        return (startTime < periodEnd && endTime > periodStart);
                    }
                    return false;
                });

                if (conflictInCurrentClass) {
                    return {
                        hasConflict: true,
                        conflictDetails: {
                            className: currentSchedule.className,
                            day: daysOfWeek[dayOfWeek],
                            time: `${conflictInCurrentClass.startTime} - ${conflictInCurrentClass.endTime}`,
                            subject: conflictInCurrentClass.subject
                        }
                    };
                }
            }

            // Check teacher's periods array (fast query!)
            const teacherRef = doc(db, 'teachers', teacherId);
            const teacherDoc = await getDoc(teacherRef);

            if (teacherDoc.exists()) {
                const teacherData = teacherDoc.data();
                const periods = teacherData.periods || [];

                const conflictPeriod = periods.find((period: any) => {
                    // Skip the period being edited
                    if (excludePeriodId && period.periodId === excludePeriodId) return false;

                    // Check if same day
                    if (period.dayOfWeek === dayOfWeek) {
                        // Check for time overlap
                        const periodStart = period.startTime;
                        const periodEnd = period.endTime;

                        return (startTime < periodEnd && endTime > periodStart);
                    }
                    return false;
                });

                if (conflictPeriod) {
                    return {
                        hasConflict: true,
                        conflictDetails: {
                            className: conflictPeriod.className,
                            day: daysOfWeek[dayOfWeek],
                            time: `${conflictPeriod.startTime} - ${conflictPeriod.endTime}`,
                            subject: conflictPeriod.subject
                        }
                    };
                }
            }

            return { hasConflict: false, conflictDetails: null };
        } catch (error) {
            console.error('Error checking teacher availability:', error);
            return { hasConflict: false, conflictDetails: null };
        }
    }, [formData.schedules, selectedClass?.id]);

    const fetchWeeklySchedule = async () => {
        if (!selectedClass) {
            showError('Error', 'Please select a class first');
            return;
        }

        setFetchingSchedule(true);
        try {
            // First check if schedule is already loaded in state
            const existingScheduleInState = formData.schedules.find(s => s.classId === selectedClass.id);
            if (existingScheduleInState && existingScheduleInState.periods.length > 0) {
                setFetchedSchedule(existingScheduleInState);
                setShowScheduleTable(true);
                showSuccess('Success', 'Weekly schedule loaded from current session!');
                setFetchingSchedule(false);
                return;
            }

            // If not in state, fetch from Firebase
            const scheduleRef = doc(db, 'schedules', selectedClass.id);
            const scheduleDoc = await getDoc(scheduleRef);

            if (scheduleDoc.exists()) {
                const scheduleData = scheduleDoc.data() as any;
                // Ensure backward compatibility by adding sessions property if missing
                const scheduleWithSessions: ClassSchedule = {
                    ...scheduleData,
                    sessions: scheduleData.sessions || []
                };
                setFetchedSchedule(scheduleWithSessions);
                setShowScheduleTable(true);
                showSuccess('Success', 'Weekly schedule fetched from database!');
            } else {
                // Try new structure: schedules/classId/sessions/
                const sessionsRef = collection(db, 'schedules', selectedClass.id, 'sessions');
                const sessionsSnapshot = await getDocs(sessionsRef);

                if (!sessionsSnapshot.empty) {
                    const allPeriods: any[] = [];
                    const allSessions: any[] = [];

                    sessionsSnapshot.forEach((sessionDoc) => {
                        const sessionData = sessionDoc.data();
                        const sessionInfo = sessionData.sessionInfo;
                        const periods = sessionData.periods || [];

                        allSessions.push(sessionInfo);
                        periods.forEach((period: any) => {
                            allPeriods.push({
                                ...period,
                                sessionId: sessionInfo.id
                            });
                        });
                    });

                    const scheduleData: ClassSchedule = {
                        id: selectedClass.id,
                        name: `${selectedClass.id} Schedule`,
                        classId: selectedClass.id,
                        className: selectedClass.id,
                        periods: allPeriods,
                        sessions: allSessions,
                        isActive: true,
                        createdAt: new Date(),
                        updatedAt: new Date()
                    };

                    setFetchedSchedule(scheduleData);
                    setShowScheduleTable(true);
                    showSuccess('Success', 'Weekly schedule fetched from new structure!');
                } else {
                    showError('Error', 'No schedule found for this class');
                }
            }
        } catch (error) {
            console.error('Error fetching schedule:', error);
            showError('Error', 'Failed to fetch weekly schedule');
        } finally {
            setFetchingSchedule(false);
        }
    };

    const loadExistingSchedule = async (classId: string) => {
        try {
            // Load sessions from the new structure: schedules/classId/sessions/
            const sessionsRef = collection(db, 'schedules', classId, 'sessions');
            const sessionsSnapshot = await getDocs(sessionsRef);

            if (!sessionsSnapshot.empty) {
                const allPeriods: any[] = [];
                const allSessions: any[] = [];

                sessionsSnapshot.forEach((sessionDoc) => {
                    const sessionData = sessionDoc.data();
                    const sessionInfo = sessionData.sessionInfo;
                    const periods = sessionData.periods || [];

                    // Add session info
                    allSessions.push(sessionInfo);

                    // Add periods with sessionId
                    periods.forEach((period: any) => {
                        allPeriods.push({
                            ...period,
                            sessionId: sessionInfo.id
                        });
                    });
                });

                const scheduleData = {
                    id: classId,
                    name: `${classId} Schedule`,
                    classId: classId,
                    className: classId,
                    periods: allPeriods,
                    sessions: allSessions,
                    isActive: true,
                    createdAt: new Date(),
                    updatedAt: new Date()
                };

                console.log('Loaded existing schedule from new structure:', scheduleData);
                return scheduleData;
            }

            // Fallback: try old structure for backward compatibility
            const scheduleRef = doc(db, 'schedules', classId);
            const scheduleDoc = await getDoc(scheduleRef);

            if (scheduleDoc.exists()) {
                const scheduleData = scheduleDoc.data() as any;

                // Migrate existing periods to have sessionId if missing
                const migratedPeriods = scheduleData.periods?.map((period: any) => {
                    if (!period.sessionId) {
                        const defaultSessionId = `default-${period.dayOfWeek}`;
                        return {
                            ...period,
                            sessionId: defaultSessionId
                        };
                    }
                    return period;
                }) || [];

                // Create default sessions for each day that has periods
                const uniqueDays = Array.from(new Set(migratedPeriods.map((p: any) => p.dayOfWeek))) as number[];
                const defaultSessions = uniqueDays.map((dayOfWeek: number) => {
                    // Get start and end time from school time configuration
                    let startTime = '08:00';
                    let endTime = '17:00';

                    if (selectedSchoolTime && selectedSchoolTime.days) {
                        const dayConfig = selectedSchoolTime.days.find(d =>
                            daysOfWeek.indexOf(d.day) === dayOfWeek
                        );
                        if (dayConfig) {
                            startTime = dayConfig.startTime || startTime;
                            endTime = dayConfig.endTime || endTime;
                        }
                    }

                    return {
                        id: `default-${dayOfWeek}`,
                        classId: classId,
                        dayOfWeek,
                        sessionNumber: 1,
                        startTime,
                        endTime,
                        name: 'Main Session'
                    };
                });

                const scheduleWithSessions = {
                    ...scheduleData,
                    periods: migratedPeriods,
                    sessions: scheduleData.sessions?.length > 0 ? scheduleData.sessions : defaultSessions
                };
                console.log('Loaded existing schedule with migration:', scheduleWithSessions);
                return scheduleWithSessions;
            }

            return null;
        } catch (error) {
            console.error('Error loading schedule:', error);
            return null;
        }
    };

    const handleClassSelect = async (cls: Class) => {
        setSelectedClass(cls);
        setCurrentStep(2);

        // Check if schedule already exists in state
        const existingScheduleInState = formData.schedules.find(s => s.classId === cls.id);
        if (existingScheduleInState) {
            return; // Schedule already loaded in state
        }

        // Try to load existing schedule from Firebase
        const existingSchedule = await loadExistingSchedule(cls.id);
        if (existingSchedule) {
            // Load existing schedule into state
            setFormData(prev => ({
                ...prev,
                schedules: [...prev.schedules, existingSchedule]
            }));
            showSuccess('Success', 'Existing schedule loaded successfully!');
        } else {
            // Create new empty schedule
            const newSchedule: ClassSchedule = {
                id: cls.id,
                name: `${cls.grade} - ${cls.section} Schedule`,
                classId: cls.id,
                className: `${cls.grade} - ${cls.section}`,
                periods: [],
                sessions: [],
                isActive: true,
                createdAt: new Date(),
                updatedAt: new Date()
            };
            setFormData(prev => ({
                ...prev,
                schedules: [...prev.schedules, newSchedule]
            }));
        }
    };

    const handlePeriodClick = (dayOfWeek: number, timeSlot: any) => {
        if (!selectedClass || !selectedSchoolTime) return;

        setSelectedDay(dayOfWeek);
        setSelectedPeriod({
            id: '',
            classId: selectedClass.id,
            className: `${selectedClass.grade} - ${selectedClass.section}`,
            subject: '',
            teacherId: '',
            teacherName: '',
            room: '',
            dayOfWeek,
            timeSlotId: timeSlot.id,
            startTime: timeSlot.startTime,
            endTime: timeSlot.endTime,
            status: 'pending',
            periodNumber: timeSlot.periodNumber || 1
        });
        setShowPeriodModal(true);
    };

    const handleEditPeriod = (period: SchedulePeriod) => {
        setSelectedPeriod(period);
        setSelectedDay(period.dayOfWeek);
        setShowPeriodModal(true);
    };

    // Helper function to check for time overlap
    const checkTimeOverlap = (startTime1: string, endTime1: string, startTime2: string, endTime2: string): boolean => {
        const start1 = new Date(`2000-01-01T${startTime1}`);
        const end1 = new Date(`2000-01-01T${endTime1}`);
        const start2 = new Date(`2000-01-01T${startTime2}`);
        const end2 = new Date(`2000-01-01T${endTime2}`);

        return (start1 < end2 && end1 > start2);
    };

    // Helper function to check if a day is full (reached end time)
    const isDayFull = (dayOfWeek: number, sessionId?: string): boolean => {
        if (!selectedClass || !selectedSchoolTime) return false;

        // Get the school day end time for this day
        const dayConfig = selectedSchoolTime.days.find(d =>
            daysOfWeek.indexOf(d.day) === dayOfWeek
        );

        if (!dayConfig || !dayConfig.endTime) return false;

        const schoolEndTime = dayConfig.endTime;

        // Get all periods for this day (and session if specified)
        const schedule = formData.schedules.find(s => s.classId === selectedClass.id);
        if (!schedule || !schedule.periods || schedule.periods.length === 0) {
            return false;
        }

        let dayPeriods = schedule.periods.filter((p: any) => p.dayOfWeek === dayOfWeek);

        // Filter by session if specified
        if (sessionId) {
            dayPeriods = dayPeriods.filter((p: any) => p.sessionId === sessionId);
        }

        if (dayPeriods.length === 0) return false;

        // Find the latest end time
        const latestEndTime = dayPeriods.reduce((latest: string, period: any) => {
            return period.endTime > latest ? period.endTime : latest;
        }, '00:00');

        // Day is full if latest end time >= school end time
        return latestEndTime >= schoolEndTime;
    };

    const handleSavePeriod = async (periodData: any) => {
        if (!selectedClass) return;

        // Get or create default session for this day
        const schedule = formData.schedules.find(s => s.classId === selectedClass.id);

        // Check for time conflicts with existing periods on the same day
        if (schedule && schedule.periods) {
            const conflictingPeriod = schedule.periods.find((p: any) => {
                // Skip checking against itself when editing
                if (selectedPeriod && p.id === selectedPeriod.id) {
                    return false;
                }

                // Only check periods on the same day
                if (p.dayOfWeek !== periodData.dayOfWeek) {
                    return false;
                }

                // Check if times overlap
                return checkTimeOverlap(
                    periodData.startTime,
                    periodData.endTime,
                    p.startTime,
                    p.endTime
                );
            });

            if (conflictingPeriod) {
                const dayName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][periodData.dayOfWeek];
                showError(
                    'Time Conflict',
                    `This time slot overlaps with another period on ${dayName}: ${conflictingPeriod.subject || 'Break'} (${conflictingPeriod.startTime} - ${conflictingPeriod.endTime})`
                );
                return;
            }
        }

        // Check if period end time exceeds school day end time
        if (selectedSchoolTime) {
            const dayConfig = selectedSchoolTime.days.find(d =>
                daysOfWeek.indexOf(d.day) === periodData.dayOfWeek
            );

            if (dayConfig && dayConfig.endTime) {
                if (periodData.endTime > dayConfig.endTime) {
                    const dayName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][periodData.dayOfWeek];
                    showError(
                        'Time Exceeds School Day',
                        `Period end time (${periodData.endTime}) exceeds the school day end time (${dayConfig.endTime}) for ${dayName}.`
                    );
                    return;
                }
            }
        }

        const sessions = schedule?.sessions || [];
        let targetSessionId = periodData.sessionId;

        // Smart session detection based on time
        let targetSession: any = null;

        if (targetSessionId) {
            // Use the provided sessionId if it exists
            targetSession = sessions.find((s: any) => s.id === targetSessionId);
            if (targetSession) {
                console.log('Using provided session:', targetSession);
            }
        }

        // If no valid session found, use smart detection
        if (!targetSession) {
            targetSession = findOrCreateSessionForTime(periodData.dayOfWeek, periodData.startTime);
            if (targetSession) {
                // Add new session to schedule if it's a new one
                const existingSession = sessions.find((s: any) => s.id === targetSession.id);
                if (!existingSession) {
                    setFormData(prev => ({
                        ...prev,
                        schedules: prev.schedules.map(s =>
                            s.classId === selectedClass.id
                                ? { ...s, sessions: [...(s.sessions || []), targetSession] }
                                : s
                        )
                    }));
                }
                targetSessionId = targetSession.id;
                console.log('Using smart detected session:', targetSession);
            }
        } else {
            targetSessionId = targetSession.id;
        }

        // Calculate period number based on existing periods for the day and session
        const sessionPeriods = schedule?.periods.filter((p: any) =>
            p.dayOfWeek === periodData.dayOfWeek && p.sessionId === targetSessionId
        ) || [];
        const periodNumber = sessionPeriods.length + 1;

        // Create period with status, period number, and sessionId
        const newPeriod: any = {
            ...periodData,
            id: selectedPeriod?.id || Date.now().toString() + Math.random(),
            status: 'pending',
            periodNumber: periodNumber,
            sessionId: targetSessionId
        };

        console.log('NEW PERIOD TO ADD:', newPeriod);
        console.log('Target session ID:', targetSessionId);

        // Handle teacher availability update BEFORE state update
        if (selectedPeriod && selectedPeriod.id && selectedPeriod.teacherId) {
            // Editing: Remove old teacher availability first
            removeTeacherAvailability(selectedPeriod.teacherId, selectedPeriod.id).then(() => {
                console.log('✅ Removed old teacher availability for:', selectedPeriod.teacherId);
            }).catch(err => {
                console.error('❌ Failed to remove old teacher availability:', err);
            });
        }

        // Update local state IMMEDIATELY
        setFormData(prev => {
            const existingScheduleIndex = prev.schedules.findIndex(s => s.classId === selectedClass.id);

            if (existingScheduleIndex >= 0) {
                // Update existing schedule
                const existingSchedule = prev.schedules[existingScheduleIndex];

                // If editing, remove the old period first
                let updatedPeriods = existingSchedule.periods || [];
                if (selectedPeriod && selectedPeriod.id) {
                    // Remove old period by ID
                    updatedPeriods = updatedPeriods.filter((p: any) => p.id !== selectedPeriod.id);
                    console.log('EDITING: Removed old period with ID:', selectedPeriod.id);
                }
                // Add the new/updated period
                updatedPeriods = [...updatedPeriods, newPeriod];

                // Auto-adjust next period if editing and there's a gap
                if (selectedPeriod && selectedPeriod.id) {
                    // Find all periods for the same day, sorted by start time
                    const sameDayPeriods = updatedPeriods
                        .filter((p: any) => p.dayOfWeek === newPeriod.dayOfWeek)
                        .sort((a: any, b: any) => a.startTime.localeCompare(b.startTime));

                    // Find the index of the updated period
                    const updatedPeriodIndex = sameDayPeriods.findIndex((p: any) => p.id === newPeriod.id);

                    if (updatedPeriodIndex >= 0 && updatedPeriodIndex < sameDayPeriods.length - 1) {
                        // There's a next period
                        const nextPeriod = sameDayPeriods[updatedPeriodIndex + 1];

                        // Check if there's a gap between the current period's end and next period's start
                        if (newPeriod.endTime < nextPeriod.startTime) {
                            console.log(`Gap detected between ${newPeriod.endTime} and ${nextPeriod.startTime}. Auto-adjusting next period.`);

                            // Calculate the duration of the next period
                            const nextPeriodDuration = calculateDuration(nextPeriod.startTime, nextPeriod.endTime);

                            // Update the next period's start time to match current period's end time
                            const adjustedNextPeriod = {
                                ...nextPeriod,
                                startTime: newPeriod.endTime,
                                endTime: calculateEndTime(newPeriod.endTime, nextPeriodDuration)
                            };

                            // Replace the next period in the array
                            updatedPeriods = updatedPeriods.map((p: any) =>
                                p.id === nextPeriod.id ? adjustedNextPeriod : p
                            );

                            console.log('Next period adjusted:', adjustedNextPeriod);
                        }
                    }
                }

                const updatedSchedule = {
                    ...existingSchedule,
                    periods: updatedPeriods,
                    updatedAt: new Date()
                };

                console.log('UPDATING EXISTING SCHEDULE:', updatedSchedule);

                const newSchedules = [...prev.schedules];
                newSchedules[existingScheduleIndex] = updatedSchedule;

                // Save to Firebase with new structure: schedules/classId/sessions/sessionId
                const saveSessionData = async () => {
                    try {
                        const sessionRef = doc(db, 'schedules', selectedClass.id, 'sessions', targetSessionId);

                        // Check if session already exists
                        const existingSessionDoc = await getDoc(sessionRef);
                        let sessionData;

                        if (existingSessionDoc.exists()) {
                            // Update existing session
                            const existingData = existingSessionDoc.data();

                            // Get all periods for this session from the updated schedule (includes auto-adjusted periods)
                            const sessionPeriods = updatedPeriods.filter((p: any) => p.sessionId === targetSessionId);

                            sessionData = {
                                ...existingData,
                                periods: sessionPeriods,
                                updatedAt: new Date()
                            };
                        } else {
                            // Create new session with times from school configuration
                            let startTime = '08:00';
                            let endTime = '17:00';

                            if (selectedSchoolTime && selectedSchoolTime.days) {
                                const dayConfig = selectedSchoolTime.days.find(d =>
                                    daysOfWeek.indexOf(d.day) === newPeriod.dayOfWeek
                                );
                                if (dayConfig) {
                                    startTime = dayConfig.startTime || startTime;
                                    endTime = dayConfig.endTime || endTime;
                                }
                            }

                            sessionData = {
                                sessionInfo: {
                                    id: targetSessionId,
                                    classId: selectedClass.id,
                                    dayOfWeek: newPeriod.dayOfWeek,
                                    sessionNumber: 1,
                                    startTime,
                                    endTime,
                                    name: 'Main Session'
                                },
                                periods: [newPeriod],
                                updatedAt: new Date()
                            };
                        }

                        await setDoc(sessionRef, sessionData);
                        console.log('Session saved successfully');
                    } catch (error) {
                        console.error('Error saving session:', error);
                        showError('Error', 'Failed to save session');
                    }
                };

                saveSessionData();

                // Add new teacher availability (old one already removed above)
                if (newPeriod.teacherId) {
                    updateTeacherAvailability(newPeriod.teacherId, {
                        classId: newPeriod.classId,
                        periodId: newPeriod.id,
                        className: newPeriod.className,
                        subject: newPeriod.subject,
                        dayOfWeek: newPeriod.dayOfWeek,
                        startTime: newPeriod.startTime,
                        endTime: newPeriod.endTime,
                        status: newPeriod.status
                    });
                }

                // Invalidate availability cache
                invalidateAvailabilityCache();

                return { ...prev, schedules: newSchedules };
            } else {
                // Create new schedule
                const newSchedule: ClassSchedule = {
                    id: selectedClass.id,
                    name: `${selectedClass.grade} - ${selectedClass.section} Schedule`,
                    classId: selectedClass.id,
                    className: `${selectedClass.grade} - ${selectedClass.section}`,
                    periods: [newPeriod],
                    sessions: [],
                    isActive: true,
                    createdAt: new Date(),
                    updatedAt: new Date()
                };

                console.log('CREATING NEW SCHEDULE:', newSchedule);

                // Save to Firebase
                const scheduleRef = doc(db, 'schedules', selectedClass.id);
                setDoc(scheduleRef, newSchedule).catch(error => {
                    console.error('Error saving schedule:', error);
                    showError('Error', 'Failed to save schedule');
                });

                // Also update teacher availability
                updateTeacherAvailability(newPeriod.teacherId, {
                    classId: newPeriod.classId,
                    periodId: newPeriod.id,
                    className: newPeriod.className,
                    subject: newPeriod.subject,
                    dayOfWeek: newPeriod.dayOfWeek,
                    startTime: newPeriod.startTime,
                    endTime: newPeriod.endTime,
                    status: newPeriod.status
                });

                // Invalidate availability cache since we added a new period
                invalidateAvailabilityCache();

                return { ...prev, schedules: [...prev.schedules, newSchedule] };
            }
        });

        setShowPeriodModal(false);
        setSelectedPeriod(null);
        showSuccess('Success', selectedPeriod ?
            `${periodData.isBreak ? 'Break' : 'Period'} updated successfully!` :
            `${periodData.isBreak ? 'Break' : 'Period'} added successfully!`);
    };

    const handleRemovePeriod = async (periodId: string) => {
        if (!selectedClass) return;

        // Find the current schedule and remove the period
        const currentSchedule = formData.schedules.find(s => s.classId === selectedClass.id);
        if (!currentSchedule) return;

        const periodToRemove = currentSchedule.periods.find(p => p.id === periodId);
        if (!periodToRemove) {
            showError('Error', 'Period not found');
            return;
        }

        // Update local state first
        const updatedSchedule: ClassSchedule = {
            ...currentSchedule,
            periods: currentSchedule.periods.filter(p => p.id !== periodId),
            updatedAt: new Date()
        };

        // Save the updated schedule to Firebase (sessions subcollection)
        try {
            const sessionId = periodToRemove.sessionId;
            if (!sessionId) {
                showError('Error', 'Session ID not found for this period');
                return;
            }

            const sessionRef = doc(db, 'schedules', selectedClass.id, 'sessions', sessionId);
            const sessionDoc = await getDoc(sessionRef);

            if (sessionDoc.exists()) {
                const sessionData = sessionDoc.data();
                const updatedPeriods = (sessionData.periods || []).filter((p: any) => p.id !== periodId);

                // Update session with filtered periods
                await setDoc(sessionRef, {
                    ...sessionData,
                    periods: updatedPeriods,
                    updatedAt: new Date()
                }, { merge: true });

                console.log('✅ Period removed from session successfully');
            }

            // Remove teacher availability BEFORE invalidating cache
            if (periodToRemove.teacherId) {
                console.log('Removing teacher availability for:', periodToRemove.teacherId, 'Period ID:', periodId);
                try {
                    await removeTeacherAvailability(periodToRemove.teacherId, periodId);
                    console.log('✅ Teacher availability removal completed');
                } catch (error) {
                    console.error('❌ Failed to remove teacher availability, but period was deleted from schedule');
                    showError('Warning', 'Period deleted but teacher availability might not be updated. Please refresh the page.');
                }
            }

            // Invalidate availability cache AFTER removing teacher availability
            invalidateAvailabilityCache();
            console.log('✅ Cache invalidated');

            // Update local state after successful save
            setFormData(prev => ({
                ...prev,
                schedules: prev.schedules.map(schedule =>
                    schedule.classId === selectedClass.id ? updatedSchedule : schedule
                )
            }));

            showSuccess('Success', 'Period and teacher availability removed successfully!');
        } catch (error) {
            console.error('❌ Error removing period:', error);
            showError('Error', 'Failed to remove period. Please try again.');
            return;
        }
    };

    const getPeriodsForDay = (dayOfWeek: number) => {
        if (!selectedClass) return [];
        const schedule = formData.schedules.find(s => s.classId === selectedClass.id);
        const periods = schedule?.periods.filter((p: any) => p.dayOfWeek === dayOfWeek) || [];
        console.log('getPeriodsForDay:', {
            dayOfWeek,
            selectedClassId: selectedClass?.id,
            schedule,
            periods,
            allSchedules: formData.schedules
        });
        return periods;
    };

    const calculateEndTime = (startTime: string, duration: number): string => {
        const [hours, minutes] = startTime.split(':').map(Number);
        const totalMinutes = hours * 60 + minutes + duration;
        const endHours = Math.floor(totalMinutes / 60);
        const endMinutes = totalMinutes % 60;
        return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
    };

    const calculateDuration = (startTime: string, endTime: string): number => {
        const [startHours, startMinutes] = startTime.split(':').map(Number);
        const [endHours, endMinutes] = endTime.split(':').map(Number);
        const startTotalMinutes = startHours * 60 + startMinutes;
        const endTotalMinutes = endHours * 60 + endMinutes;
        return endTotalMinutes - startTotalMinutes;
    };

    const updateTimeSlot = (dayOfWeek: number, slotId: string, startTime: string, endTime: string) => {
        // Update the time slot in the generated slots
        // This is a local update for display purposes
        // In a real implementation, you'd want to update the school time configuration

        // For now, we'll just update the period data if it exists
        const schedule = formData.schedules.find(s => s.classId === selectedClass?.id);
        if (schedule) {
            const period = schedule.periods.find((p: any) => p.timeSlotId === slotId && p.dayOfWeek === dayOfWeek);
            if (period) {
                period.startTime = startTime;
                period.endTime = endTime;

                // Update the schedule in state
                setFormData(prev => ({
                    ...prev,
                    schedules: prev.schedules.map(s =>
                        s.classId === selectedClass?.id
                            ? {
                                ...s, periods: s.periods.map(p =>
                                    p.timeSlotId === slotId && p.dayOfWeek === dayOfWeek
                                        ? { ...p, startTime, endTime }
                                        : p
                                )
                            }
                            : s
                    )
                }));
            }
        }
    };

    const handleCopyMondaySchedule = (targetDayOfWeek: number) => {
        if (!selectedClass) return;
        setCopyTargetDay(targetDayOfWeek);
        setShowCopyConfirmModal(true);
    };

    const handleConfirmCopy = () => {
        if (copyTargetDay !== null) {
            copyMondaySchedule(copyTargetDay);
        }
        setShowCopyConfirmModal(false);
    };

    const handleCancelCopy = () => {
        setShowCopyConfirmModal(false);
        setCopyTargetDay(null);
    };

    const copyMondaySchedule = async (targetDayOfWeek: number) => {
        if (!selectedClass || !selectedSchoolTime) return;

        const schedule = formData.schedules.find(s => s.classId === selectedClass.id);
        if (!schedule) return;

        // Validate that Monday and target day have the same end time
        const mondayConfig = selectedSchoolTime.days.find(d => daysOfWeek.indexOf(d.day) === 1);
        const targetDayConfig = selectedSchoolTime.days.find(d => daysOfWeek.indexOf(d.day) === targetDayOfWeek);

        if (!mondayConfig || !targetDayConfig) {
            showError('Configuration Error', 'Unable to find school time configuration for Monday or target day.');
            return;
        }

        if (mondayConfig.endTime !== targetDayConfig.endTime) {
            const targetDayName = daysOfWeek[targetDayOfWeek];
            showError(
                'School Time Mismatch',
                `Cannot copy Monday's schedule to ${targetDayName}. Monday ends at ${mondayConfig.endTime} but ${targetDayName} ends at ${targetDayConfig.endTime}. Please ensure both days have the same end time before copying.`
            );
            return;
        }

        // Set loading state
        setCopyingToDay(targetDayOfWeek);

        try {
            // Get Monday's sessions and periods
            const mondaySessions = schedule.sessions?.filter((s: any) => s.dayOfWeek === 1) || [];
            const mondayPeriods = schedule.periods.filter((p: any) => p.dayOfWeek === 1);

            if (mondayPeriods.length === 0) {
                showError('No Schedule', 'Monday has no periods to copy.');
                setCopyingToDay(null);
                return;
            }

            // Check if target day already has periods
            const targetDayPeriods = schedule.periods.filter((p: any) => p.dayOfWeek === targetDayOfWeek);
            if (targetDayPeriods.length > 0) {
                showError('Day Not Empty', `${daysOfWeek[targetDayOfWeek]} already has periods. Please delete them first before copying.`);
                setCopyingToDay(null);
                return;
            }

            // Create new sessions for the target day
            const newSessions = mondaySessions.map((session: any) => ({
                ...session,
                id: `session-${targetDayOfWeek}-${session.sessionNumber}-${Date.now()}`,
                dayOfWeek: targetDayOfWeek
            }));

            // Map old session IDs to new session IDs
            const sessionIdMap: Record<string, string> = {};
            mondaySessions.forEach((oldSession: any, index: number) => {
                sessionIdMap[oldSession.id] = newSessions[index].id;
            });

            // Create new periods for the target day with correct session IDs
            const newPeriods = mondayPeriods.map((period: any) => {
                const newSessionId = sessionIdMap[period.sessionId] || newSessions[0]?.id || `default-${targetDayOfWeek}`;
                return {
                    ...period,
                    id: Date.now().toString() + Math.random(), // Generate new ID
                    dayOfWeek: targetDayOfWeek,
                    status: 'pending',
                    sessionId: newSessionId
                };
            });

            console.log('Copying to Firebase:', { newSessions, newPeriods });

            // Delete existing sessions for the target day from Firebase
            const existingTargetSessions = schedule.sessions?.filter((s: any) => s.dayOfWeek === targetDayOfWeek) || [];
            for (const session of existingTargetSessions) {
                const sessionRef = doc(db, 'schedules', selectedClass.id, 'sessions', session.id);
                await deleteDoc(sessionRef);
                console.log('Deleted existing session:', session.id);
            }

            // Remove teacher availability for existing periods on target day
            const existingTargetPeriods = schedule.periods.filter((p: any) => p.dayOfWeek === targetDayOfWeek);
            for (const period of existingTargetPeriods) {
                if (period.teacherId) {
                    await removeTeacherAvailability(period.teacherId, period.id);
                    console.log('Removed teacher availability for:', period.teacherId);
                }
            }

            // Save new sessions to Firebase
            for (const session of newSessions) {
                const sessionPeriods = newPeriods.filter((p: any) => p.sessionId === session.id);

                const sessionData = {
                    sessionInfo: {
                        id: session.id,
                        classId: selectedClass.id,
                        dayOfWeek: targetDayOfWeek,
                        sessionNumber: session.sessionNumber,
                        startTime: targetDayConfig.startTime || '08:00',
                        endTime: targetDayConfig.endTime || '17:00',
                        name: session.name || 'Main Session'
                    },
                    periods: sessionPeriods,
                    updatedAt: new Date()
                };

                const sessionRef = doc(db, 'schedules', selectedClass.id, 'sessions', session.id);
                await setDoc(sessionRef, sessionData);
                console.log('Saved session to Firebase:', session.id);
            }

            // Add teacher availability for new periods
            for (const period of newPeriods) {
                if (period.teacherId) {
                    await updateTeacherAvailability(period.teacherId, {
                        classId: period.classId,
                        periodId: period.id,
                        className: period.className,
                        subject: period.subject,
                        dayOfWeek: period.dayOfWeek,
                        startTime: period.startTime,
                        endTime: period.endTime,
                        status: period.status
                    });
                    console.log('Added teacher availability for:', period.teacherId);
                }
            }

            // Invalidate availability cache
            invalidateAvailabilityCache();

            // Remove existing sessions and periods for the target day
            const filteredSessions = schedule.sessions?.filter((s: any) => s.dayOfWeek !== targetDayOfWeek) || [];
            const filteredPeriods = schedule.periods.filter((p: any) => p.dayOfWeek !== targetDayOfWeek);

            // Add new sessions and periods
            const updatedSessions = [...filteredSessions, ...newSessions];
            const updatedPeriods = [...filteredPeriods, ...newPeriods];

            // Update the local state
            setFormData(prev => ({
                ...prev,
                schedules: prev.schedules.map(s =>
                    s.classId === selectedClass.id
                        ? { ...s, sessions: updatedSessions, periods: updatedPeriods, updatedAt: new Date() }
                        : s
                )
            }));

            showSuccess('Success', `${daysOfWeek[targetDayOfWeek]}'s schedule copied from Monday and saved to Firebase!`);
        } catch (error) {
            console.error('Error copying schedule:', error);
            showError('Error', 'Failed to copy schedule. Please try again.');
        } finally {
            // Clear loading state
            setCopyingToDay(null);
        }
    };

    // Helper functions for day status management
    const getDayOffStatus = (dayOfWeek: number): boolean => {
        return dayStatuses[dayOfWeek]?.isDayOff || false;
    };


    const getLastPeriodEndTime = (dayOfWeek: number, sessionId?: string): string => {
        if (sessionId) {
            return getLastPeriodEndTimeForSession(dayOfWeek, sessionId);
        }

        const schedule = formData.schedules.find(s => s.classId === selectedClass?.id);
        if (!schedule) return '08:00'; // Default start time

        const dayPeriods = schedule.periods
            .filter((p: any) => p.dayOfWeek === dayOfWeek)
            .sort((a: any, b: any) => a.startTime.localeCompare(b.startTime));

        if (dayPeriods.length === 0) return '08:00';

        return dayPeriods[dayPeriods.length - 1].endTime;
    };

    const toggleDayOff = (dayOfWeek: number) => {
        setDayStatuses(prev => ({
            ...prev,
            [dayOfWeek]: {
                ...prev[dayOfWeek],
                isDayOff: !prev[dayOfWeek]?.isDayOff,
                copyFromMonday: prev[dayOfWeek]?.isDayOff ? prev[dayOfWeek]?.copyFromMonday : false
            }
        }));
    };


    const handleAddPeriod = (dayOfWeek: number, sessionId?: string) => {
        if (!selectedClass) return;

        console.log(`Adding period for day ${dayOfWeek}, sessionId: ${sessionId}`);

        // Get default start time from school time configuration
        let defaultStartTime = '08:00'; // Fallback
        if (selectedSchoolTime && selectedSchoolTime.days) {
            const dayConfig = selectedSchoolTime.days.find(d =>
                daysOfWeek.indexOf(d.day) === dayOfWeek
            );
            if (dayConfig && dayConfig.startTime) {
                defaultStartTime = dayConfig.startTime;
            }
        }

        // Get the last end time for the specified session or find the best time
        let startTime = defaultStartTime;

        if (sessionId) {
            // Use the specific session's last end time
            startTime = getLastPeriodEndTime(dayOfWeek, sessionId);
        } else {
            // Find the best time to start based on existing periods
            const schedule = formData.schedules.find(s => s.classId === selectedClass.id);
            if (schedule) {
                const dayPeriods = schedule.periods
                    .filter((p: any) => p.dayOfWeek === dayOfWeek)
                    .sort((a: any, b: any) => b.endTime.localeCompare(a.endTime));

                if (dayPeriods.length > 0) {
                    startTime = dayPeriods[0].endTime; // Start after the last period
                }
            }
        }

        // Calculate the correct period number for this session
        const schedule = formData.schedules.find(s => s.classId === selectedClass.id);
        let periodNumber = 1;

        if (schedule && sessionId) {
            // Count existing periods in this specific session
            const sessionPeriods = schedule.periods.filter((p: any) =>
                p.dayOfWeek === dayOfWeek && p.sessionId === sessionId
            );
            periodNumber = sessionPeriods.length + 1;
        }

        console.log(`Setting periodNumber to ${periodNumber} for session ${sessionId}`);

        setSelectedDay(dayOfWeek);
        setSelectedPeriod({
            id: '',
            classId: selectedClass.id,
            className: `${selectedClass.grade} - ${selectedClass.section}`,
            subject: '',
            teacherId: '',
            teacherName: '',
            room: '',
            dayOfWeek,
            timeSlotId: '',
            startTime: startTime,
            endTime: '',
            status: 'pending',
            periodNumber: periodNumber,
            isBreak: false,
            sessionId: sessionId || ''
        });
        setShowPeriodModal(true);
    };

    // Session management functions
    const getSessionsForDay = (dayOfWeek: number) => {
        if (!selectedClass) return [];
        const schedule = formData.schedules.find(s => s.classId === selectedClass.id);
        if (!schedule) return [];

        // Return existing sessions for this day
        const existingSessions = schedule.sessions?.filter((s: any) => s.dayOfWeek === dayOfWeek) || [];

        // If no sessions exist, return empty array (don't create default here)
        // The default session will be created when adding the first period
        return existingSessions.sort((a: any, b: any) => a.sessionNumber - b.sessionNumber);
    };

    const getPeriodsForSession = (dayOfWeek: number, sessionId: string) => {
        if (!selectedClass) return [];
        const schedule = formData.schedules.find(s => s.classId === selectedClass.id);
        if (!schedule) return [];

        const sessionPeriods = schedule.periods.filter((p: any) =>
            p.dayOfWeek === dayOfWeek && p.sessionId === sessionId
        ).sort((a: any, b: any) => a.startTime.localeCompare(b.startTime));

        console.log(`Getting periods for session ${sessionId} on day ${dayOfWeek}:`, sessionPeriods);
        return sessionPeriods;
    };

    const getSessionTimeRange = (dayOfWeek: number, sessionId: string) => {
        const periods = getPeriodsForSession(dayOfWeek, sessionId);

        if (periods.length === 0) {
            return { startTime: '00:00', endTime: '00:00' };
        }

        const startTime = periods[0].startTime; // First period start time
        const endTime = periods[periods.length - 1].endTime; // Last period end time

        return { startTime, endTime };
    };

    const getLastPeriodEndTimeForSession = (dayOfWeek: number, sessionId: string): string => {
        const sessionPeriods = getPeriodsForSession(dayOfWeek, sessionId);
        if (sessionPeriods.length === 0) {
            const sessions = getSessionsForDay(dayOfWeek);
            const session = sessions.find((s: any) => s.id === sessionId);
            if (session?.startTime) {
                return session.startTime;
            }

            // Get default from school time configuration
            let defaultStartTime = '08:00';
            if (selectedSchoolTime && selectedSchoolTime.days) {
                const dayConfig = selectedSchoolTime.days.find(d =>
                    daysOfWeek.indexOf(d.day) === dayOfWeek
                );
                if (dayConfig && dayConfig.startTime) {
                    defaultStartTime = dayConfig.startTime;
                }
            }
            return defaultStartTime;
        }

        return sessionPeriods[sessionPeriods.length - 1].endTime;
    };

    const findOrCreateSessionForTime = (dayOfWeek: number, startTime: string) => {
        if (!selectedClass) return null;

        const schedule = formData.schedules.find(s => s.classId === selectedClass.id);
        if (!schedule) return null;

        const sessions = schedule.sessions?.filter((s: any) => s.dayOfWeek === dayOfWeek) || [];
        const periods = schedule.periods.filter((p: any) => p.dayOfWeek === dayOfWeek);

        // If no sessions exist, create the first one
        if (sessions.length === 0) {
            const newSession = {
                id: `session-${dayOfWeek}-1-${Date.now()}`,
                classId: selectedClass.id,
                dayOfWeek,
                sessionNumber: 1,
                startTime: startTime,
                endTime: startTime, // Will be updated as periods are added
                name: 'Main Session'
            };
            return newSession;
        }

        // Find if this time fits into an existing session
        for (const session of sessions) {
            const sessionPeriods = periods.filter((p: any) => p.sessionId === session.id);
            if (sessionPeriods.length === 0) {
                // Empty session, use it
                return session;
            }

            // Get the last period of this session
            const lastPeriod = sessionPeriods.sort((a: any, b: any) =>
                b.endTime.localeCompare(a.endTime)
            )[0];

            // Check if the new period can fit consecutively
            if (startTime === lastPeriod.endTime) {
                return session; // Perfect fit, no gap
            }

            // Check if there's a small gap (within 30 minutes) - still same session
            const lastEndMinutes = timeToMinutes(lastPeriod.endTime);
            const newStartMinutes = timeToMinutes(startTime);
            if (newStartMinutes - lastEndMinutes <= 30) {
                return session; // Small gap, same session
            }
        }

        // No suitable session found, create a new one
        const newSessionNumber = sessions.length + 1;
        const newSession = {
            id: `session-${dayOfWeek}-${newSessionNumber}-${Date.now()}`,
            classId: selectedClass.id,
            dayOfWeek,
            sessionNumber: newSessionNumber,
            startTime: startTime,
            endTime: startTime, // Will be updated as periods are added
            name: `Session ${newSessionNumber}`
        };

        return newSession;
    };

    const timeToMinutes = (time: string): number => {
        const [hours, minutes] = time.split(':').map(Number);
        return hours * 60 + minutes;
    };



    const handleAddSession = (dayOfWeek: number) => {
        if (!selectedClass) return;

        const sessions = getSessionsForDay(dayOfWeek);
        const sessionNumber = sessions.length + 1;

        const newSession = {
            id: `session-${dayOfWeek}-${sessionNumber}-${Date.now()}`,
            classId: selectedClass.id,
            dayOfWeek,
            sessionNumber,
            startTime: '00:00', // Will be calculated from periods
            endTime: '00:00',   // Will be calculated from periods
            name: `Session ${sessionNumber}`
        };

        console.log('Creating new session:', newSession);
        console.log('Existing sessions for day:', sessions);

        // Add session to the schedule
        setFormData(prev => ({
            ...prev,
            schedules: prev.schedules.map(s =>
                s.classId === selectedClass.id
                    ? { ...s, sessions: [...(s.sessions || []), newSession] }
                    : s
            )
        }));

        showSuccess('Success', `Session ${sessionNumber} added successfully!`);
    };

    const handleDeleteSession = (dayOfWeek: number, sessionId: string, sessionName: string) => {
        setSessionToDelete({ dayOfWeek, sessionId, sessionName });
        setShowDeleteSessionModal(true);
    };

    const confirmDeleteSession = async () => {
        if (!sessionToDelete || !selectedClass) return;

        try {
            // Remove session from local state
            setFormData(prev => ({
                ...prev,
                schedules: prev.schedules.map(s =>
                    s.classId === selectedClass.id
                        ? {
                            ...s,
                            sessions: s.sessions.filter(session => session.id !== sessionToDelete.sessionId),
                            periods: s.periods.filter(period => period.sessionId !== sessionToDelete.sessionId)
                        }
                        : s
                )
            }));

            // Delete session from Firebase
            const sessionRef = doc(db, 'schedules', selectedClass.id, 'sessions', sessionToDelete.sessionId);
            await deleteDoc(sessionRef);

            showSuccess('Success', `Session "${sessionToDelete.sessionName}" deleted successfully`);
        } catch (error) {
            console.error('Error deleting session:', error);
            showError('Error', 'Failed to delete session');
        } finally {
            setShowDeleteSessionModal(false);
            setSessionToDelete(null);
        }
    };

    const cancelDeleteSession = () => {
        setShowDeleteSessionModal(false);
        setSessionToDelete(null);
    };


    const generateAvailableTimeSlots = (dayOfWeek: number) => {
        if (!selectedSchoolTime) return [];

        // Find the day configuration
        const dayConfig = selectedSchoolTime.days.find(day => {
            const dayIndex = daysOfWeek.indexOf(day.day);
            return dayIndex === dayOfWeek;
        });

        if (!dayConfig || !dayConfig.isActive) return [];

        const slots = [];
        const startHour = parseInt(dayConfig.startTime.split(':')[0]);
        const endHour = parseInt(dayConfig.endTime.split(':')[0]);
        let periodNumber = 1;

        // Generate hourly slots
        for (let hour = startHour; hour < endHour; hour++) {
            const startTime = `${hour.toString().padStart(2, '0')}:00`;
            const endTime = `${(hour + 1).toString().padStart(2, '0')}:00`;

            // Check if this time conflicts with break times for this specific day
            const conflictsWithBreak = dayConfig.breakTimes?.some(breakTime => {
                const breakStart = breakTime.startTime;
                const breakEnd = breakTime.endTime;
                return (startTime >= breakStart && startTime < breakEnd) ||
                    (endTime > breakStart && endTime <= breakEnd) ||
                    (startTime <= breakStart && endTime >= breakEnd);
            });

            if (!conflictsWithBreak) {
                slots.push({
                    id: `period-${periodNumber}`,
                    name: `Period ${periodNumber}`,
                    startTime,
                    endTime,
                    duration: 60,
                    periodNumber: periodNumber
                });
                periodNumber++;
            }
        }

        return slots;
    };

    const exportScheduleToPDF = () => {
        if (!selectedSchoolTime) {
            showError('Error', 'School time configuration not found');
            return;
        }

        if (!formData.schedules || formData.schedules.length === 0) {
            showError('Error', 'No schedules available to export');
            return;
        }

        try {
            const doc = new jsPDF('l', 'mm', 'a4'); // landscape orientation
            const currentDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });

            let startY = 15;

            // Loop through all class schedules
            formData.schedules.forEach((schedule, classIndex) => {
                if (!schedule.periods || schedule.periods.length === 0) return;

                // Add new page for each class except the first
                if (classIndex > 0) {
                    doc.addPage();
                    startY = 15;
                }

                // Title
                doc.setFontSize(16);
                doc.setFont('helvetica', 'bold');
                doc.text(`CLASS SCHEDULE (${currentDate.toUpperCase()})`, doc.internal.pageSize.width / 2, startY, { align: 'center' });

                startY += 10;

                // Class Name
                doc.setFontSize(14);
                doc.setFont('helvetica', 'bold');
                doc.text(`${schedule.className}`, 15, startY);

                startY += 5;

                // Group periods by day for this class
                const dayGroups: { [key: string]: SchedulePeriod[] } = {};
                daysOfWeek.forEach((_, index) => {
                    dayGroups[index] = schedule.periods.filter((p: any) => p.dayOfWeek === index);
                });

                // Check school time configuration for each day
                const dayConfigs = selectedSchoolTime.days;

                // Check if Mon-Thu have identical schedules
                const monPeriods = dayGroups[1];
                const haveSameMonThu = [2, 3, 4].every(day => {
                    const dayPeriods = dayGroups[day];
                    if (dayPeriods.length !== monPeriods.length) return false;
                    return dayPeriods.every((p: any, i: number) => {
                        const monP = monPeriods[i];
                        return p.subject === monP.subject &&
                            p.startTime === monP.startTime &&
                            p.endTime === monP.endTime;
                    });
                });

                // Get unique time slots across all days
                const uniqueTimeSlots = new Set<string>();
                schedule.periods.forEach((p: any) => {
                    uniqueTimeSlots.add(`${p.startTime}-${p.endTime}`);
                });
                const sortedTimeSlots = Array.from(uniqueTimeSlots).sort();

                // Prepare table headers
                const headers = [['Day/Time', ...sortedTimeSlots]];

                // Prepare table body
                const tableBody: any[][] = [];

                // Add MON-THU row if they have the same schedule
                if (haveSameMonThu && monPeriods.length > 0) {
                    const monThuCells = ['MON-THU'];
                    sortedTimeSlots.forEach(timeSlot => {
                        const [start, end] = timeSlot.split('-');
                        const period = monPeriods.find((p: any) =>
                            p.startTime === start && p.endTime === end
                        );
                        if (period) {
                            monThuCells.push(`${period.subject || ''}\n${period.teacherName || ''}`);
                        } else {
                            monThuCells.push('');
                        }
                    });
                    tableBody.push(monThuCells);
                } else {
                    // Add individual rows for Mon-Thu if different
                    [1, 2, 3, 4].forEach(day => {
                        const dayName = daysOfWeek[day].toUpperCase();
                        const dayPeriods = dayGroups[day];
                        if (dayPeriods.length > 0) {
                            const dayCells = [dayName];
                            sortedTimeSlots.forEach(timeSlot => {
                                const [start, end] = timeSlot.split('-');
                                const period = dayPeriods.find((p: any) =>
                                    p.startTime === start && p.endTime === end
                                );
                                if (period) {
                                    dayCells.push(`${period.subject || ''}\n${period.teacherName || ''}`);
                                } else {
                                    dayCells.push('');
                                }
                            });
                            tableBody.push(dayCells);
                        }
                    });
                }

                // Add FRIDAY row if periods exist
                const fridayPeriods = dayGroups[5];
                if (fridayPeriods.length > 0) {
                    const fridayCells = ['FRIDAY'];
                    sortedTimeSlots.forEach(timeSlot => {
                        const [start, end] = timeSlot.split('-');
                        const period = fridayPeriods.find((p: any) =>
                            p.startTime === start && p.endTime === end
                        );
                        if (period) {
                            fridayCells.push(`${period.subject || ''}\n${period.teacherName || ''}`);
                        } else {
                            fridayCells.push('');
                        }
                    });
                    tableBody.push(fridayCells);
                }

                // Add SATURDAY row
                const saturdayPeriods = dayGroups[6];
                if (saturdayPeriods.length > 0) {
                    const satCells = ['SATURDAY'];
                    sortedTimeSlots.forEach(timeSlot => {
                        const [start, end] = timeSlot.split('-');
                        const period = saturdayPeriods.find((p: any) =>
                            p.startTime === start && p.endTime === end
                        );
                        if (period) {
                            satCells.push(`${period.subject || ''}\n${period.teacherName || ''}`);
                        } else {
                            satCells.push('');
                        }
                    });
                    tableBody.push(satCells);
                } else {
                    const satConfig = dayConfigs.find(d => daysOfWeek.indexOf(d.day) === 6);
                    if (!satConfig || !satConfig.isActive) {
                        tableBody.push(['SATURDAY (DAY OFF)', ...sortedTimeSlots.map(() => 'NO CLASSES')]);
                    }
                }

                // Add SUNDAY row
                const sundayPeriods = dayGroups[0];
                if (sundayPeriods.length > 0) {
                    const sunCells = ['SUNDAY'];
                    sortedTimeSlots.forEach(timeSlot => {
                        const [start, end] = timeSlot.split('-');
                        const period = sundayPeriods.find((p: any) =>
                            p.startTime === start && p.endTime === end
                        );
                        if (period) {
                            sunCells.push(`${period.subject || ''}\n${period.teacherName || ''}`);
                        } else {
                            sunCells.push('');
                        }
                    });
                    tableBody.push(sunCells);
                } else {
                    const sunConfig = dayConfigs.find(d => daysOfWeek.indexOf(d.day) === 0);
                    if (!sunConfig || !sunConfig.isActive) {
                        tableBody.push(['SUNDAY (DAY OFF)', ...sortedTimeSlots.map(() => 'NO CLASSES')]);
                    }
                }

                // Generate table for this class
                autoTable(doc, {
                    startY: startY,
                    head: headers,
                    body: tableBody,
                    theme: 'grid',
                    styles: {
                        fontSize: 10,
                        cellPadding: 3,
                        overflow: 'linebreak',
                        halign: 'center',
                        valign: 'middle',
                        font: 'helvetica',
                        lineColor: [0, 0, 0],
                        lineWidth: 0.1
                    },
                    headStyles: {
                        fillColor: [200, 200, 200],
                        textColor: [0, 0, 0],
                        fontStyle: 'bold',
                        halign: 'center',
                        fontSize: 10
                    },
                    columnStyles: {
                        0: {
                            fontStyle: 'bold',
                            fillColor: [240, 240, 240],
                            halign: 'left',
                            cellWidth: 30
                        }
                    },
                    margin: { left: 15, right: 15 },
                    // Use didParseCell to handle Arabic text rendering
                    didParseCell: function (data: any) {
                        // Check if cell contains Arabic/Urdu characters
                        const cellText = data.cell.text.join('');
                        const hasArabic = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(cellText);

                        if (hasArabic) {
                            // Increase font size for better Arabic text visibility
                            data.cell.styles.fontSize = 11;
                            // Add more padding for better rendering
                            data.cell.styles.cellPadding = 4;
                        }
                    }
                });
            });

            // Save PDF with all classes
            const dateStr = new Date().toLocaleDateString('en-GB').replace(/\//g, '-');
            doc.save(`All_Classes_Schedule_${dateStr}.pdf`);
            showSuccess('Success', 'All schedules exported to PDF successfully!');
        } catch (error) {
            console.error('Error generating PDF:', error);
            showError('Error', 'Failed to generate PDF');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Progress Steps */}
            <div className="flex items-center justify-center space-x-4 mb-8">
                <div className={`flex items-center space-x-2 ${currentStep >= 1 ? 'text-emerald-600' : 'text-gray-400'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 1 ? 'bg-emerald-600 text-white' : 'bg-gray-200'}`}>
                        1
                    </div>
                    <span className="text-sm font-medium">Select Class</span>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-400" />
                <div className={`flex items-center space-x-2 ${currentStep >= 2 ? 'text-emerald-600' : 'text-gray-400'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 2 ? 'bg-emerald-600 text-white' : 'bg-gray-200'}`}>
                        2
                    </div>
                    <span className="text-sm font-medium">Create Schedule</span>
                </div>
            </div>

            {/* Step 1: Class Selection */}
            {currentStep === 1 && (
                <Card glassy={true} className="islamic-pattern">
                    <div className="p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Class</h3>
                        <p className="text-sm text-gray-600 mb-6">Choose the class for which you want to create a schedule.</p>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {classes.map(cls => (
                                <div
                                    key={cls.id}
                                    onClick={() => handleClassSelect(cls)}
                                    className="p-4 border border-gray-200 rounded-lg hover:border-emerald-400 hover:bg-emerald-50 cursor-pointer transition-colors"
                                >
                                    <div className="flex items-center space-x-3">
                                        <div className="p-2 bg-emerald-100 rounded-lg">
                                            <Users className="h-5 w-5 text-emerald-600" />
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-gray-900">{cls.grade} - {cls.section}</h4>
                                            <p className="text-sm text-gray-600">{cls.subjects?.length || 0} subjects</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </Card>
            )}

            {/* Step 2: Schedule Creation */}
            {currentStep === 2 && selectedClass && (
                <div className="space-y-6">
                    {/* Class Info */}
                    <Card glassy={true} className="islamic-pattern">
                        <div className="p-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <div className="p-2 bg-emerald-100 rounded-lg">
                                        <Users className="h-6 w-6 text-emerald-600" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900">{selectedClass.grade} - {selectedClass.section}</h3>
                                        <p className="text-sm text-gray-600">
                                            {(() => {
                                                const existingSchedule = formData.schedules.find(s => s.classId === selectedClass.id);
                                                const periodCount = existingSchedule?.periods.length || 0;
                                                return periodCount > 0
                                                    ? `Schedule loaded - ${periodCount} periods scheduled`
                                                    : 'Creating schedule for this class';
                                            })()}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-3">
                                    {/* <Button
                                        variant="primary"
                                        onClick={exportScheduleToPDF}
                                        className="flex items-center space-x-2"
                                    >
                                        <FileText className="h-4 w-4" />
                                        <span>Export PDF</span>
                                    </Button> */}
                                    {/* <Button
                                        variant="secondary"
                                        onClick={fetchWeeklySchedule}
                                        disabled={fetchingSchedule}
                                        className="flex items-center space-x-2"
                                    >
                                        {fetchingSchedule ? (
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600"></div>
                                        ) : (
                                            <Download className="h-4 w-4" />
                                        )}
                                        <span>{fetchingSchedule ? 'Fetching...' : 'Fetch Weekly Schedule'}</span>
                                    </Button> */}
                                    <Button
                                        variant="secondary"
                                        onClick={() => setCurrentStep(1)}
                                    >
                                        <ChevronLeft className="h-4 w-4 mr-2" />
                                        Back
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Schedule Grid */}
                    <Card glassy={true} className="islamic-pattern">
                        <div className="p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Weekly Schedule</h3>

                            <div className="overflow-x-auto">
                                <div className="space-y-4">
                                    {daysOfWeek.map((day, dayIndex) => {
                                        const dayOfWeek = dayIndex;
                                        const timeSlots = generateAvailableTimeSlots(dayOfWeek);

                                        if (timeSlots.length === 0) return null;

                                        // Find the day configuration
                                        const dayConfig = selectedSchoolTime?.days.find(d => {
                                            const dayIndex = daysOfWeek.indexOf(d.day);
                                            return dayIndex === dayOfWeek;
                                        });

                                        const periods = getPeriodsForDay(dayOfWeek);
                                        const isDayOff = getDayOffStatus(dayOfWeek);

                                        return (
                                            <div key={day} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                                                <div className="bg-gradient-to-r from-emerald-50 to-blue-50 px-6 py-4 border-b border-gray-200">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center space-x-3">
                                                            <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                                                            <h4 className="text-lg font-semibold text-gray-900">{day}</h4>
                                                        </div>
                                                        <div className="flex items-center space-x-4">
                                                            <div className="flex items-center space-x-4">
                                                                {dayOfWeek !== 1 && dayOfWeek !== 0 && periods.length === 0 && (
                                                                    <button
                                                                        onClick={() => handleCopyMondaySchedule(dayOfWeek)}
                                                                        disabled={copyingToDay === dayOfWeek}
                                                                        className={`px-3 py-1 text-white text-xs rounded-lg transition-colors flex items-center space-x-1 ${copyingToDay === dayOfWeek
                                                                            ? 'bg-gray-400 cursor-not-allowed'
                                                                            : 'bg-emerald-600 hover:bg-emerald-700'
                                                                            }`}
                                                                    >
                                                                        {copyingToDay === dayOfWeek ? (
                                                                            <>
                                                                                <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></div>
                                                                                <span>Copying...</span>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <Calendar className="h-3 w-3" />
                                                                                <span>Copy Monday</span>
                                                                            </>
                                                                        )}
                                                                    </button>
                                                                )}
                                                                <label className="flex items-center space-x-2 cursor-pointer group">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isDayOff}
                                                                        onChange={() => toggleDayOff(dayOfWeek)}
                                                                        className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                                                                    />
                                                                    <span className="text-sm font-medium text-gray-700 group-hover:text-red-700 transition-colors">
                                                                        Day Off
                                                                    </span>
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="p-6 relative">
                                                    {/* Loading overlay when copying */}
                                                    {copyingToDay === dayOfWeek && (
                                                        <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-10 rounded-b-xl">
                                                            <div className="text-center">
                                                                <div className="animate-spin h-12 w-12 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                                                                <div className="text-lg font-semibold text-gray-900 mb-2">Copying Schedule...</div>
                                                                <div className="text-sm text-gray-600">Please wait while we copy Monday's schedule</div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {isDayOff ? (
                                                        <div className="text-center py-12">
                                                            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                                                                <X className="h-8 w-8 text-red-600" />
                                                            </div>
                                                            <div className="text-xl font-semibold text-gray-900 mb-2">Day Off</div>
                                                            <div className="text-gray-500">No classes scheduled for this day</div>
                                                        </div>
                                                    ) : (
                                                        <div className="space-y-6">
                                                            {/* Sessions */}
                                                            {getSessionsForDay(dayOfWeek).length > 0 ? (
                                                                getSessionsForDay(dayOfWeek).map((session: any) => {
                                                                    const sessionPeriods = getPeriodsForSession(dayOfWeek, session.id);

                                                                    return (
                                                                        <div key={session.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                                                            {/* Session Header */}
                                                                            <div className="flex items-center justify-between mb-4">
                                                                                <div className="flex items-center space-x-3">
                                                                                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                                                                                    <div>
                                                                                        <h5 className="font-semibold text-gray-900">{session.name}</h5>
                                                                                        <div className="text-sm text-gray-600 flex items-center space-x-1">
                                                                                            <Clock className="h-3 w-3" />
                                                                                            {(() => {
                                                                                                const timeRange = getSessionTimeRange(dayOfWeek, session.id);
                                                                                                if (timeRange.startTime === '00:00' && timeRange.endTime === '00:00') {
                                                                                                    return <span>No periods yet</span>;
                                                                                                }
                                                                                                return <span>{formatTo12Hour(timeRange.startTime)} - {formatTo12Hour(timeRange.endTime)}</span>;
                                                                                            })()}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                                <div className="flex items-center space-x-2">
                                                                                    {!isDayFull(dayOfWeek, session.id) && (
                                                                                        <button
                                                                                            onClick={() => handleAddPeriod(dayOfWeek, session.id)}
                                                                                            className="px-3 py-1 bg-emerald-600 text-white text-xs rounded-lg hover:bg-emerald-700 transition-colors flex items-center space-x-1"
                                                                                        >
                                                                                            <Plus className="h-3 w-3" />
                                                                                            <span>Add Period</span>
                                                                                        </button>
                                                                                    )}
                                                                                    {getSessionsForDay(dayOfWeek).length > 1 && (
                                                                                        <button
                                                                                            onClick={() => handleDeleteSession(dayOfWeek, session.id, session.name)}
                                                                                            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                                                                            title="Delete session"
                                                                                        >
                                                                                            <X className="h-4 w-4" />
                                                                                        </button>
                                                                                    )}
                                                                                </div>
                                                                            </div>

                                                                            {/* Session Periods */}
                                                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                                                                                {sessionPeriods.map((period: any) => (
                                                                                    <div key={period.id} className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow">
                                                                                        {/* Time Header */}
                                                                                        <div className="flex items-center justify-between mb-2">
                                                                                            <div className="flex items-center space-x-1">
                                                                                                <Clock className="h-3 w-3 text-gray-400" />
                                                                                                <span className="text-xs font-semibold text-gray-900">
                                                                                                    {formatTo12Hour(period.startTime)} - {formatTo12Hour(period.endTime)}
                                                                                                </span>
                                                                                            </div>
                                                                                            <div className="flex items-center space-x-1">
                                                                                                <button
                                                                                                    onClick={() => handleEditPeriod(period)}
                                                                                                    className="p-1 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                                                                                                    title="Edit period"
                                                                                                >
                                                                                                    <Edit className="h-3 w-3" />
                                                                                                </button>
                                                                                                <button
                                                                                                    onClick={() => handleRemovePeriod(period.id)}
                                                                                                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                                                                                    title="Remove period"
                                                                                                >
                                                                                                    <X className="h-3 w-3" />
                                                                                                </button>
                                                                                            </div>
                                                                                        </div>

                                                                                        {/* Type Badge */}
                                                                                        <div className="mb-2">
                                                                                            <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${period.isBreak
                                                                                                ? 'bg-orange-100 text-orange-700'
                                                                                                : 'bg-blue-100 text-blue-700'
                                                                                                }`}>
                                                                                                {period.isBreak ? 'Break' : 'Period'}
                                                                                            </div>
                                                                                        </div>

                                                                                        {/* Subject and Teacher (only for class periods) */}
                                                                                        {!period.isBreak && (
                                                                                            <div className="space-y-1">
                                                                                                <div className="flex items-center space-x-1">
                                                                                                    <BookOpen className="h-3 w-3 text-gray-400" />
                                                                                                    <span className="text-xs text-gray-700 truncate">
                                                                                                        {period.subject || 'No subject'}
                                                                                                    </span>
                                                                                                </div>
                                                                                                <div className="flex items-center space-x-1">
                                                                                                    <Users className="h-3 w-3 text-gray-400" />
                                                                                                    <span className="text-xs text-gray-600 truncate">
                                                                                                        {period.teacherName || 'No teacher'}
                                                                                                    </span>
                                                                                                </div>
                                                                                            </div>
                                                                                        )}

                                                                                        {/* Break message */}
                                                                                        {period.isBreak && (
                                                                                            <div className="text-xs text-orange-600 italic">
                                                                                                Rest period
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                ))}

                                                                                {/* Add Period Button for Session */}
                                                                                {!isDayFull(dayOfWeek, session.id) ? (
                                                                                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-emerald-400 hover:bg-emerald-50/50 transition-colors min-h-[120px] flex items-center justify-center">
                                                                                        <button
                                                                                            onClick={() => handleAddPeriod(dayOfWeek, session.id)}
                                                                                            className="flex flex-col items-center justify-center text-gray-500 hover:text-emerald-600 transition-colors group"
                                                                                        >
                                                                                            <div className="w-8 h-8 bg-gray-100 group-hover:bg-emerald-100 rounded-full flex items-center justify-center mb-2 transition-colors">
                                                                                                <Plus className="h-4 w-4" />
                                                                                            </div>
                                                                                            <span className="font-medium text-xs">Add Period</span>
                                                                                            <span className="text-xs text-gray-400 text-center mt-1 leading-tight">
                                                                                                Click to add<br />period or break
                                                                                            </span>
                                                                                        </button>
                                                                                    </div>
                                                                                ) : (
                                                                                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50 min-h-[120px] flex items-center justify-center">
                                                                                        <div className="flex flex-col items-center justify-center text-gray-400">
                                                                                            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center mb-2">
                                                                                                <Clock className="h-4 w-4" />
                                                                                            </div>
                                                                                            <span className="font-medium text-xs">Day Complete</span>
                                                                                            <span className="text-xs text-center mt-1 leading-tight">
                                                                                                Schedule has reached<br />school end time
                                                                                            </span>
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })
                                                            ) : (
                                                                /* No Sessions Message */
                                                                <div className="text-center py-12">
                                                                    <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                                                                        <Clock className="h-8 w-8 text-gray-400" />
                                                                    </div>
                                                                    <div className="text-lg font-semibold text-gray-900 mb-2">No Sessions Yet</div>
                                                                    <div className="text-gray-500 mb-4">Create your first session to start adding periods</div>
                                                                    <button
                                                                        onClick={() => handleAddPeriod(dayOfWeek)}
                                                                        className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center space-x-2 mx-auto"
                                                                    >
                                                                        <Plus className="h-4 w-4" />
                                                                        <span>Add First Period</span>
                                                                    </button>
                                                                </div>
                                                            )}

                                                            {/* Add Session Button - Only show if there are existing sessions */}
                                                            {getSessionsForDay(dayOfWeek).length > 0 && (
                                                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-400 hover:bg-blue-50/50 transition-colors">
                                                                    <button
                                                                        onClick={() => handleAddSession(dayOfWeek)}
                                                                        className="flex items-center justify-center w-full text-gray-500 hover:text-blue-600 transition-colors group"
                                                                    >
                                                                        <div className="flex items-center space-x-3">
                                                                            <div className="w-10 h-10 bg-gray-100 group-hover:bg-blue-100 rounded-full flex items-center justify-center transition-colors">
                                                                                <Plus className="h-5 w-5" />
                                                                            </div>
                                                                            <div className="text-left">
                                                                                <div className="font-medium text-sm">Add Session</div>
                                                                                <div className="text-xs text-gray-400">Create a new session for this day</div>
                                                                            </div>
                                                                        </div>
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Subject Summary */}
                    <Card glassy={true} className="islamic-pattern">
                        <div className="p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Subject Schedule Summary</h3>

                            <div className="space-y-3">
                                {selectedClass?.subjects?.map((subject) => {
                                    const scheduledPeriods = formData.schedules
                                        .find(s => s.classId === selectedClass?.id)?.periods
                                        .filter((p: any) => p.subject === subject) || [];

                                    return (
                                        <div key={subject} className="p-3 border border-gray-200 rounded-lg">
                                            <div className="flex items-center justify-between mb-2">
                                                <h4 className="font-medium text-gray-900">{subject}</h4>
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${scheduledPeriods.length > 0
                                                    ? 'bg-emerald-100 text-emerald-800'
                                                    : 'bg-gray-100 text-gray-600'
                                                    }`}>
                                                    {scheduledPeriods.length > 0 ? 'Scheduled' : 'Not Scheduled'}
                                                </span>
                                            </div>

                                            {scheduledPeriods.length > 0 ? (
                                                <div className="space-y-1">
                                                    {scheduledPeriods.map((period: any) => (
                                                        <div key={period.id} className="text-sm text-gray-600">
                                                            {daysOfWeek[period.dayOfWeek]} - {formatTo12Hour(period.startTime)} to {formatTo12Hour(period.endTime)}
                                                            <span className="text-gray-500"> (Teacher: {period.teacherName})</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-sm text-gray-500">
                                                    No schedule assigned for this subject
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </Card>

                    {/* Navigation */}
                    <div className="flex justify-start">
                        <Button
                            variant="secondary"
                            onClick={() => setCurrentStep(1)}
                        >
                            <ChevronLeft className="h-4 w-4 mr-2" />
                            Back
                        </Button>
                    </div>
                </div>
            )}


            {/* Period Modal */}
            {showPeriodModal && selectedClass && selectedSchoolTime && (
                <PeriodModal
                    class={selectedClass}
                    dayOfWeek={selectedDay}
                    schoolTime={selectedSchoolTime}
                    period={selectedPeriod}
                    teachers={teachers}
                    checkTeacherAvailability={checkTeacherAvailability}
                    getCachedAvailability={getCachedAvailability}
                    setCachedAvailability={setCachedAvailability}
                    onSave={handleSavePeriod}
                    onCancel={() => setShowPeriodModal(false)}
                />
            )}

            {/* Copy Monday Confirmation Modal */}
            {showCopyConfirmModal && copyTargetDay !== null && (
                <Modal
                    isOpen={true}
                    onClose={handleCancelCopy}
                    title="Copy Monday's Schedule"
                    size="sm"
                >
                    <div className="space-y-4">
                        <div className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg">
                            <div className="flex-shrink-0">
                                <Calendar className="h-8 w-8 text-blue-600" />
                            </div>
                            <div>
                                <h3 className="text-base font-semibold text-gray-900">
                                    Copy Schedule to {daysOfWeek[copyTargetDay]}
                                </h3>
                                <p className="text-sm text-gray-600 mt-1">
                                    This will copy all periods from Monday to {daysOfWeek[copyTargetDay]}.
                                </p>
                            </div>
                        </div>

                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <div className="flex items-start space-x-2">
                                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                                <div className="text-sm text-yellow-800">
                                    <p className="font-medium mb-1">Please note:</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>All periods will be copied with the same times</li>
                                        <li>Teacher assignments will be preserved</li>
                                        <li>This action cannot be undone</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-end space-x-3 pt-2">
                            <button
                                onClick={handleCancelCopy}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmCopy}
                                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium flex items-center space-x-2"
                            >
                                <Calendar className="h-4 w-4" />
                                <span>Yes, Copy Schedule</span>
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Schedule Table Modal */}
            {showScheduleTable && fetchedSchedule && (
                <ScheduleTableModal
                    schedule={fetchedSchedule}
                    onClose={() => setShowScheduleTable(false)}
                />
            )}

            {/* Delete Session Confirmation Modal */}
            {showDeleteSessionModal && sessionToDelete && (
                <Modal
                    isOpen={showDeleteSessionModal}
                    onClose={cancelDeleteSession}
                    title="Delete Session"
                >
                    <div className="p-6">
                        <div className="flex items-center space-x-3 mb-6">
                            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                                <X className="h-6 w-6 text-red-600" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">Delete Session</h3>
                                <p className="text-sm text-gray-500">This action cannot be undone</p>
                            </div>
                        </div>

                        <div className="mb-6">
                            <p className="text-gray-700">
                                Are you sure you want to delete <strong>"{sessionToDelete.sessionName}"</strong>?
                            </p>
                            <p className="text-sm text-red-600 mt-2">
                                This will also delete all periods in this session.
                            </p>
                        </div>

                        <div className="flex items-center justify-end space-x-3">
                            <button
                                onClick={cancelDeleteSession}
                                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDeleteSession}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                            >
                                Yes, Delete Session
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

// Period Modal Component
interface PeriodModalProps {
    class: Class;
    dayOfWeek: number;
    schoolTime: SchoolTime;
    period: SchedulePeriod | null;
    teachers: any[];
    checkTeacherAvailability: (teacherId: string, dayOfWeek: number, startTime: string, endTime: string, excludePeriodId?: string) => Promise<{ hasConflict: boolean, conflictDetails: { className: string, day: string, time: string, subject: string } | null }>;
    getCachedAvailability: (dayOfWeek: number, startTime: string, endTime: string) => Record<string, { hasConflict: boolean, conflictDetails: { className: string, day: string, time: string, subject: string } | null }> | null;
    setCachedAvailability: (dayOfWeek: number, startTime: string, endTime: string, data: Record<string, { hasConflict: boolean, conflictDetails: { className: string, day: string, time: string, subject: string } | null }>) => void;
    onSave: (period: Omit<SchedulePeriod, 'id'>) => void;
    onCancel: () => void;
}

const PeriodModal: React.FC<PeriodModalProps> = ({
    class: cls,
    dayOfWeek,
    schoolTime,
    period,
    teachers,
    checkTeacherAvailability,
    getCachedAvailability,
    setCachedAvailability,
    onSave,
    onCancel
}) => {
    const [formData, setFormData] = useState({
        subject: period?.subject || '',
        teacherId: period?.teacherId || '',
        teacherName: period?.teacherName || '',
        startTime: period?.startTime || '',
        endTime: period?.endTime || '',
        timeSlotId: period?.timeSlotId || '',
        periodNumber: period?.periodNumber || 1,
        isBreak: period?.isBreak || false,
        duration: 40 // Default 40 minutes
    });
    const [showTeacherModal, setShowTeacherModal] = useState(false);

    const getPeriodNumberInSession = (): number => {
        if (!period || !period.sessionId) {
            console.log('No period or sessionId, returning 1');
            return 1;
        }

        // If it's an existing period (has ID), use its periodNumber
        if (period.id) {
            const periodNumber = period.periodNumber || 1;
            console.log('Existing period periodNumber:', periodNumber, 'period:', period);
            return periodNumber;
        }

        // For new periods (no ID), we need to determine if this should be 2nd+ period
        // This requires access to the current schedule data to count existing periods
        console.log('New period (no ID) - need to determine if 2nd+ period');
        console.log('Current period data:', period);

        // For now, we'll use a different approach - check if this is being added to a session
        // that already has periods by looking at the periodNumber field
        if (period.periodNumber && period.periodNumber > 1) {
            console.log('New period with periodNumber > 1:', period.periodNumber);
            return period.periodNumber;
        }

        console.log('New period, assuming first period');
        return 1; // Default to first period
    };

    // Update formData when period changes
    useEffect(() => {
        setFormData({
            subject: period?.subject || '',
            teacherId: period?.teacherId || '',
            teacherName: period?.teacherName || '',
            startTime: period?.startTime || '',
            endTime: period?.endTime || '',
            timeSlotId: period?.timeSlotId || '',
            periodNumber: period?.periodNumber || 1,
            isBreak: period?.isBreak || false,
            duration: period?.endTime && period?.startTime ?
                getDuration(period.startTime, period.endTime) : 40
        });
    }, [period]);

    const getDuration = (startTime: string, endTime: string): number => {
        const [startHours, startMinutes] = startTime.split(':').map(Number);
        const [endHours, endMinutes] = endTime.split(':').map(Number);
        const startTotalMinutes = startHours * 60 + startMinutes;
        const endTotalMinutes = endHours * 60 + endMinutes;
        return endTotalMinutes - startTotalMinutes;
    };

    const calculateEndTime = (startTime: string, durationMinutes: number): string => {
        const [startHours, startMinutes] = startTime.split(':').map(Number);
        const totalMinutes = startHours * 60 + startMinutes + durationMinutes;
        const endHours = Math.floor(totalMinutes / 60);
        const endMinutes = totalMinutes % 60;
        return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
    };

    const generateAvailableTimeSlots = () => {
        // Find the day configuration
        const dayConfig = schoolTime.days.find(day => {
            const dayIndex = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].indexOf(day.day);
            return dayIndex === dayOfWeek;
        });

        if (!dayConfig || !dayConfig.isActive) return [];

        const slots = [];
        const startHour = parseInt(dayConfig.startTime.split(':')[0]);
        const endHour = parseInt(dayConfig.endTime.split(':')[0]);
        let periodNumber = 1;

        for (let hour = startHour; hour < endHour; hour++) {
            const startTime = `${hour.toString().padStart(2, '0')}:00`;
            const endTime = `${(hour + 1).toString().padStart(2, '0')}:00`;

            // Check if this time conflicts with break times for this specific day
            const conflictsWithBreak = dayConfig.breakTimes?.some(breakTime => {
                const breakStart = breakTime.startTime;
                const breakEnd = breakTime.endTime;
                return (startTime >= breakStart && startTime < breakEnd) ||
                    (endTime > breakStart && endTime <= breakEnd) ||
                    (startTime <= breakStart && endTime >= breakEnd);
            });

            if (!conflictsWithBreak) {
                slots.push({
                    id: `period-${periodNumber}`,
                    name: `Period ${periodNumber}`,
                    startTime,
                    endTime,
                    duration: 60,
                    periodNumber: periodNumber
                });
                periodNumber++;
            }
        }

        return slots;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.startTime || formData.duration <= 0) {
            return;
        }

        if (!formData.isBreak && !formData.subject) {
            return;
        }

        const endTime = calculateEndTime(formData.startTime, formData.duration);
        const periodNumber = 1; // Will be calculated based on existing periods

        const periodData: any = {
            classId: cls.id,
            className: `${cls.grade} - ${cls.section}`,
            subject: formData.isBreak ? 'Break' : formData.subject,
            teacherId: formData.isBreak ? '' : formData.teacherId,
            teacherName: formData.isBreak ? '' : formData.teacherName,
            room: '', // Not needed for now
            dayOfWeek,
            timeSlotId: '',
            startTime: formData.startTime,
            endTime: endTime,
            status: 'pending',
            periodNumber: periodNumber,
            isBreak: formData.isBreak
        };

        onSave(periodData);
    };

    const timeSlots = generateAvailableTimeSlots();

    return (
        <Modal
            isOpen={true}
            onClose={onCancel}
            title={`${period ? 'Edit' : 'Add'} Period - ${cls.grade} - ${cls.section}`}
            size="md"
        >
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Period Type */}
                <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-semibold text-gray-900 mb-3">
                        Period Type
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                        <label className={`flex items-center space-x-3 cursor-pointer p-3 rounded-lg border-2 transition-colors ${!formData.isBreak
                            ? 'border-emerald-500 bg-emerald-50'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}>
                            <input
                                type="radio"
                                name="periodType"
                                checked={!formData.isBreak}
                                onChange={() => setFormData(prev => ({ ...prev, isBreak: false, subject: '', teacherId: '', teacherName: '' }))}
                                className="w-4 h-4 text-emerald-600 border-gray-300 focus:ring-emerald-500"
                            />
                            <div className="flex items-center space-x-2">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    <BookOpen className="h-4 w-4 text-blue-600" />
                                </div>
                                <span className="font-medium text-gray-900">Class Period</span>
                            </div>
                        </label>
                        <label className={`flex items-center space-x-3 cursor-pointer p-3 rounded-lg border-2 transition-colors ${formData.isBreak
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-200 hover:border-gray-300'
                            }`}>
                            <input
                                type="radio"
                                name="periodType"
                                checked={formData.isBreak}
                                onChange={() => setFormData(prev => ({ ...prev, isBreak: true, subject: '', teacherId: '', teacherName: '' }))}
                                className="w-4 h-4 text-orange-600 border-gray-300 focus:ring-orange-500"
                            />
                            <div className="flex items-center space-x-2">
                                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                                    <Coffee className="h-4 w-4 text-orange-600" />
                                </div>
                                <span className="font-medium text-gray-900">Break</span>
                            </div>
                        </label>
                    </div>
                </div>

                {/* Time Configuration */}
                <div className="bg-gradient-to-r from-blue-50 to-emerald-50 rounded-lg p-4 border border-blue-200">
                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center space-x-2">
                                <Clock className="h-4 w-4 text-blue-600" />
                                <span>Start Time *</span>
                            </label>
                            <div className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg font-semibold text-gray-700 flex items-center cursor-not-allowed">
                                {formData.startTime ? convertTimeInput(formData.startTime) : 'N/A'}
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center space-x-2">
                                <Timer className="h-4 w-4 text-emerald-600" />
                                <span>Duration (min)</span>
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="180"
                                value={formData.duration}
                                onChange={(e) => {
                                    const duration = parseInt(e.target.value) || 40;
                                    const endTime = calculateEndTime(formData.startTime, duration);
                                    setFormData(prev => ({ ...prev, duration, endTime }));
                                }}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-medium"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center space-x-2">
                                <Clock className="h-4 w-4 text-gray-600" />
                                <span>End Time</span>
                            </label>
                            <div className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg font-semibold text-gray-700 flex items-center cursor-not-allowed">
                                {formatTo12Hour(calculateEndTime(formData.startTime, formData.duration))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Subject Selection (only for class periods) */}
                {!formData.isBreak && (
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center space-x-2">
                            <BookOpen className="h-4 w-4 text-blue-600" />
                            <span>Subject *</span>
                        </label>
                        <select
                            value={formData.subject}
                            onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-medium"
                            required
                        >
                            <option value="">Select Subject</option>
                            {cls.subjects?.map((subject) => (
                                <option key={subject} value={subject}>
                                    {subject}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Teacher Selection (only for class periods) */}
                {!formData.isBreak && (
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center space-x-2">
                            <Users className="h-4 w-4 text-emerald-600" />
                            <span>Teacher</span>
                        </label>
                        <div className="flex space-x-3">
                            <input
                                type="text"
                                value={formData.teacherName}
                                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 font-medium"
                                placeholder="No teacher selected"
                                readOnly
                            />
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => setShowTeacherModal(true)}
                                className="px-6 py-3"
                            >
                                <Users className="h-4 w-4 mr-2" />
                                Select Teacher
                            </Button>
                        </div>
                    </div>
                )}

                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={onCancel}
                        className="px-6 py-3"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        className="px-8 py-3 bg-gradient-to-r from-emerald-600 to-blue-600 hover:from-emerald-700 hover:to-blue-700"
                    >
                        {period ? 'Update' : 'Add'} {formData.isBreak ? 'Break' : 'Period'}
                    </Button>
                </div>
            </form>

            {/* Teacher Selection Modal */}
            {showTeacherModal && (
                <TeacherSelectionModal
                    teachers={teachers}
                    dayOfWeek={dayOfWeek}
                    startTime={formData.startTime}
                    endTime={formData.endTime}
                    excludePeriodId={period?.id}
                    checkTeacherAvailability={checkTeacherAvailability}
                    getCachedAvailability={getCachedAvailability}
                    setCachedAvailability={setCachedAvailability}
                    onSelect={(teacher) => {
                        setFormData(prev => ({
                            ...prev,
                            teacherId: teacher.id,
                            teacherName: `${teacher.firstName} ${teacher.lastName}`
                        }));
                        setShowTeacherModal(false);
                    }}
                    onCancel={() => setShowTeacherModal(false)}
                />
            )}
        </Modal>
    );
};

// Teacher Selection Modal Component
interface TeacherSelectionModalProps {
    teachers: any[];
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    excludePeriodId?: string;
    checkTeacherAvailability: (teacherId: string, dayOfWeek: number, startTime: string, endTime: string, excludePeriodId?: string) => Promise<{ hasConflict: boolean, conflictDetails: { className: string, day: string, time: string, subject: string } | null }>;
    getCachedAvailability: (dayOfWeek: number, startTime: string, endTime: string) => Record<string, { hasConflict: boolean, conflictDetails: { className: string, day: string, time: string, subject: string } | null }> | null;
    setCachedAvailability: (dayOfWeek: number, startTime: string, endTime: string, data: Record<string, { hasConflict: boolean, conflictDetails: { className: string, day: string, time: string, subject: string } | null }>) => void;
    onSelect: (teacher: any) => void;
    onCancel: () => void;
}

const TeacherSelectionModal: React.FC<TeacherSelectionModalProps> = ({
    teachers,
    dayOfWeek,
    startTime,
    endTime,
    excludePeriodId,
    checkTeacherAvailability,
    getCachedAvailability,
    setCachedAvailability,
    onSelect,
    onCancel
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [teacherConflicts, setTeacherConflicts] = useState<Record<string, { hasConflict: boolean, conflictDetails: { className: string, day: string, time: string, subject: string } | null }>>({});
    const [loadingConflicts, setLoadingConflicts] = useState(false);

    const filteredTeachers = teachers.filter(teacher =>
        `${teacher.firstName} ${teacher.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        teacher.teacherId?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Check conflicts for all teachers when modal opens
    useEffect(() => {
        const checkAllConflicts = async () => {
            // First, check if we have cached data
            const cachedData = getCachedAvailability(dayOfWeek, startTime, endTime);

            if (cachedData) {
                console.log('Using cached availability data - no loading needed!');
                setTeacherConflicts(cachedData);
                setLoadingConflicts(false);
                return;
            }

            // If no cache, fetch fresh data
            console.log('No cached data found - fetching fresh availability data');
            setLoadingConflicts(true);
            const conflicts: Record<string, { hasConflict: boolean, conflictDetails: { className: string, day: string, time: string, subject: string } | null }> = {};

            try {
                // Add timeout to prevent infinite loading
                const timeoutPromise = new Promise((_, reject) =>
                    setTimeout(() => reject(new Error('Timeout')), 10000) // 10 second timeout
                );

                const checkPromise = (async () => {
                    for (const teacher of filteredTeachers) {
                        try {
                            console.log('Checking teacher:', teacher.firstName, teacher.lastName);
                            const conflictResult = await checkTeacherAvailability(teacher.id, dayOfWeek, startTime, endTime, excludePeriodId);
                            conflicts[teacher.id] = conflictResult;
                        } catch (error) {
                            console.error('Error checking conflict for teacher:', teacher.id, error);
                            conflicts[teacher.id] = { hasConflict: false, conflictDetails: null };
                        }
                    }
                })();

                await Promise.race([checkPromise, timeoutPromise]);

                // Cache the results for future use
                setCachedAvailability(dayOfWeek, startTime, endTime, conflicts);
            } catch (error) {
                console.error('Error in checkAllConflicts:', error);
                // Set all teachers as available if there's an error
                filteredTeachers.forEach(teacher => {
                    conflicts[teacher.id] = { hasConflict: false, conflictDetails: null };
                });
            } finally {
                setTeacherConflicts(conflicts);
                setLoadingConflicts(false);
            }
        };

        if (filteredTeachers.length > 0) {
            checkAllConflicts();
        } else {
            setLoadingConflicts(false);
        }
    }, [filteredTeachers, dayOfWeek, startTime, endTime, excludePeriodId, checkTeacherAvailability, getCachedAvailability, setCachedAvailability]);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg w-full max-w-2xl shadow-xl">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900">Select Teacher</h2>
                    <button
                        onClick={onCancel}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="h-5 w-5 text-gray-500" />
                    </button>
                </div>

                {/* Search */}
                <div className="px-6 py-4 border-b border-gray-200">
                    <input
                        type="text"
                        placeholder="Search teachers..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                </div>

                {/* Teachers List */}
                <div className="max-h-96 overflow-y-auto">
                    {loadingConflicts ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600 mx-auto mb-2"></div>
                                <p className="text-sm text-gray-600">Checking availability...</p>
                            </div>
                        </div>
                    ) : filteredTeachers.length > 0 ? (
                        <div className="p-4 space-y-2">
                            {filteredTeachers.map((teacher) => {
                                const conflictData = teacherConflicts[teacher.id] || { hasConflict: false, conflictDetails: null };
                                const hasConflict = conflictData.hasConflict;
                                const conflictDetails = conflictData.conflictDetails;

                                return (
                                    <div
                                        key={teacher.id}
                                        onClick={() => !hasConflict && onSelect(teacher)}
                                        className={`p-3 border rounded-lg transition-colors ${hasConflict
                                            ? 'border-red-300 bg-red-50 cursor-not-allowed'
                                            : 'border-gray-200 hover:bg-gray-50 cursor-pointer'
                                            }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-3">
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${hasConflict
                                                    ? 'bg-red-100 text-red-700'
                                                    : 'bg-emerald-100 text-emerald-700'
                                                    }`}>
                                                    {teacher.firstName.charAt(0)}{teacher.lastName.charAt(0)}
                                                </div>
                                                <div>
                                                    <h4 className={`font-medium ${hasConflict ? 'text-red-900' : 'text-gray-900'}`}>
                                                        {teacher.firstName} {teacher.lastName}
                                                    </h4>
                                                    <p className={`text-sm ${hasConflict ? 'text-red-700' : 'text-gray-600'}`}>
                                                        ID: {teacher.teacherId}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                {hasConflict && (
                                                    <span className="text-xs text-red-600 font-medium">BUSY</span>
                                                )}
                                                {!hasConflict && (
                                                    <div className="w-4 h-4 rounded-full border-2 border-gray-300"></div>
                                                )}
                                            </div>
                                        </div>
                                        {hasConflict && conflictDetails && (
                                            <div className="mt-2 p-2 bg-red-100 rounded text-xs text-red-700">
                                                Already assigned to: {conflictDetails.className} - {conflictDetails.day} {conflictDetails.time}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="flex items-center justify-center py-8">
                            <p className="text-gray-500">No teachers found</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                    <Button variant="secondary" onClick={onCancel}>
                        Cancel
                    </Button>
                </div>
            </div>
        </div>
    );
};

// Schedule Table Modal Component
interface ScheduleTableModalProps {
    schedule: ClassSchedule;
    onClose: () => void;
}

const ScheduleTableModal: React.FC<ScheduleTableModalProps> = ({
    schedule,
    onClose
}) => {
    const daysOfWeek = [
        'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
    ];

    // Group periods by day
    const periodsByDay = schedule.periods.reduce((acc, period) => {
        const dayName = daysOfWeek[period.dayOfWeek];
        if (!acc[dayName]) {
            acc[dayName] = [];
        }
        acc[dayName].push(period);
        return acc;
    }, {} as Record<string, SchedulePeriod[]>);

    // Sort periods by start time within each day
    Object.keys(periodsByDay).forEach(day => {
        periodsByDay[day].sort((a, b) => a.startTime.localeCompare(b.startTime));
    });

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            title={`Weekly Schedule - ${schedule.className}`}
            size="xl"
        >
            <div className="space-y-6">
                {/* Schedule Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                            <span className="font-medium text-gray-700">Class:</span>
                            <p className="text-gray-900">{schedule.className}</p>
                        </div>
                        <div>
                            <span className="font-medium text-gray-700">Total Periods:</span>
                            <p className="text-gray-900">{schedule.periods.length}</p>
                        </div>
                        <div>
                            <span className="font-medium text-gray-700">Last Updated:</span>
                            <p className="text-gray-900">{schedule.updatedAt.toLocaleDateString()}</p>
                        </div>
                    </div>
                </div>

                {/* Schedule Table */}
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Day
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Period
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Time
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Subject
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Teacher
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {Object.entries(periodsByDay).map(([day, periods]) => (
                                periods.map((period, index) => (
                                    <tr key={`${day}-${index}`} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {index === 0 ? day : ''}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            Period {period.periodNumber}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {formatTo12Hour(period.startTime)} - {formatTo12Hour(period.endTime)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {period.subject}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {period.teacherName}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${period.status === 'pending'
                                                ? 'bg-yellow-100 text-yellow-800'
                                                : 'bg-green-100 text-green-800'
                                                }`}>
                                                {period.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Summary */}
                <div className="bg-emerald-50 p-4 rounded-lg">
                    <h4 className="font-medium text-emerald-900 mb-2">Schedule Summary</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                            <span className="font-medium text-emerald-700">Days with classes:</span>
                            <p className="text-emerald-900">{Object.keys(periodsByDay).length} days</p>
                        </div>
                        <div>
                            <span className="font-medium text-emerald-700">Total periods:</span>
                            <p className="text-emerald-900">{schedule.periods.length} periods</p>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <Button
                        variant="secondary"
                        onClick={onClose}
                    >
                        Close
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default ProfessionalScheduleBuilder;
